import { SelectField } from '@ff-it/form';
import { Box, Stack } from '@ff-it/ui';
import { ItemLoader, UpdateContainer } from 'components';
import { SectionCard } from 'components/SectionCard';
import type { NotificationMode, NotificationPreferences } from 'modules/notifications/types';
import type { Option } from 'options';
import type { ReactElement } from 'react';
import { fetcher, useUser } from 'services';

const url = 'profile/notifications/';
const modeOptions: Option<NotificationMode>[] = [
  { value: 'OFF', label: 'Off' },
  { value: 'IN_APP', label: 'In app' },
  { value: 'IN_APP_EMAIL', label: 'In app + email' },
];

function PreferenceFields({ groups }: Pick<NotificationPreferences, 'groups'>): ReactElement {
  return (
    <Stack space="3" mb="3">
      {groups.map((group) => (
        <SectionCard key={group.label} title={group.label} expanded>
          {group.types.map((preference, index) => (
            <Box key={preference.type} px="3" py="2" borderTop={index > 0 ? 'subtle' : undefined}>
              <div className="row align-items-center">
                <label htmlFor={`notification-${preference.type}`} className="col-12 col-md-6 mb-1 mb-md-0">
                  {preference.label}
                </label>
                <div className="col-12 col-md-6">
                  <SelectField
                    name={`modes[${JSON.stringify(preference.type)}]`}
                    inputId={`notification-${preference.type}`}
                    options={modeOptions}
                    simple
                    isClearable={false}
                    isSearchable={false}
                    className="mb-0"
                  />
                </div>
              </div>
            </Box>
          ))}
        </SectionCard>
      ))}
    </Stack>
  );
}

export function Notifications(): ReactElement {
  const user = useUser();
  return (
    <ItemLoader<NotificationPreferences>
      key={user.id}
      url={url}
      swrKey={[url, user.id]}
      swrOptions={{
        fetcher: () => fetcher<NotificationPreferences>({ url, method: 'GET' }),
        revalidateOnReconnect: false,
      }}
    >
      {({ item, setItem }) => (
        <UpdateContainer<NotificationPreferences>
          url={url}
          method="PATCH"
          breadcrumb="Notifications"
          className="pt-3"
          initialValues={item}
          updateItem={setItem}
          keepDirtyOnReinitialize
          successMessage="Notification preferences have been saved."
          mapValues={(values: NotificationPreferences, form) => ({
            modes: Object.fromEntries(
              Object.entries(values.modes).filter(
                ([type, mode]) => mode !== form.getState().initialValues?.modes?.[type],
              ),
            ),
          })}
        >
          <p className="text-muted">
            Choose how you receive notifications across all your agencies. Existing inbox notifications are kept.
          </p>
          <div className="col-12 col-md-8 col-xl-6 px-0">
            <PreferenceFields groups={item.groups} />
          </div>
        </UpdateContainer>
      )}
    </ItemLoader>
  );
}
